import { useDispatch } from 'react-redux';
import { pushSubscriptionModifyEventToDataLayer } from '../../common/analytics.js';
import { CommonErrorType } from '../../common/enums.js';
import { invalidPhoneNumberMsg, t } from '../../common/i18n/index.js';
import { convertStringMapToCommonErrors } from '../../common/utils/errorUtils.js';
import { PHONE_NUMBER_REGEX } from '../../common/utils/phoneNumberUtils.js';
import { SubscriptionPbxConfiguration } from '../../generated/api/models.js';
import { changeSubscriptionPbxConfiguration, loadNumberRange } from '../../selfservice/actions/index.js';
import { PbxNumberSettingsEditor } from './PbxNumberSettingsEditor.js';
import { fromKludgyConfig, toKludgyConfig } from './PbxNumberSettingsUtil.js';
import { PbxNumberSettingsView } from './PbxNumberSettingsView.js';
import type { DropdownKludgeSubscriptionPbxConfiguration } from './PbxNumberSettingsUtil.js';
import type { CommonError } from '../../common/types/errors.js';
import type { RangeNumbersState } from '../../common/types/states.js';
import type { Subscription } from '../../generated/api/models.js';

import './PbxNumberSettingsAccordionContent.scss';

const getErrorsFromPbxNumberSettings = (
  pbxConfig: SubscriptionPbxConfiguration,
  validationErrors?: { [s: string]: string }
) => {
  const errors: CommonError[] = [];
  if (
    pbxConfig.busyAction === SubscriptionPbxConfiguration.BusyActionEnum.CONNECT_TO_NUMBER &&
    (!pbxConfig.connectToNumber || !PHONE_NUMBER_REGEX.test(pbxConfig.connectToNumber))
  ) {
    errors.push({
      field: 'connectToNumber',
      message: t.YLCX(invalidPhoneNumberMsg),
      parent: 'pbxSettings',
      type: CommonErrorType.VALIDATION,
    });
  }

  const convertedValidationErrors = convertStringMapToCommonErrors(validationErrors);
  if (convertedValidationErrors) {
    errors.push(...convertedValidationErrors);
  }

  return errors.length > 0 ? errors : undefined;
};

export type PbxNumberSettingsAccordionContentProps = {
  extensionNumberRange?: RangeNumbersState;
  subscriptionPbxConfiguration: SubscriptionPbxConfiguration;
  numberRange?: RangeNumbersState;
  subscriptionId: string;
  subscriptionDisplayId: string;
  editing?: boolean;
  errors?: CommonError[];
  pbxSolution: Subscription;
  saving?: boolean;

  onCancel: () => void;
  onEdit?: () => void;
};

export const PbxNumberSettingsAccordionContent = ({
  editing,
  extensionNumberRange,
  subscriptionPbxConfiguration: properConfig,
  numberRange,
  onCancel,
  onEdit,
  subscriptionId,
  subscriptionDisplayId,
  saving,
}: PbxNumberSettingsAccordionContentProps) => {
  const subscriptionPbxConfiguration = toKludgyConfig(properConfig);
  const dispatch = useDispatch();

  const onStartEdit = onEdit
    ? () => {
        if (subscriptionPbxConfiguration.corporateNumberRangeId && !numberRange) {
          dispatch(loadNumberRange(subscriptionPbxConfiguration.corporateNumberRangeId));
        }

        if (subscriptionPbxConfiguration.extensionRangeId && !extensionNumberRange) {
          dispatch(loadNumberRange(subscriptionPbxConfiguration.extensionRangeId));
        }

        onEdit();
      }
    : undefined;

  const onConfirmEdit = (kludgyConfig: DropdownKludgeSubscriptionPbxConfiguration) => {
    pushSubscriptionModifyEventToDataLayer('vakio number settings');
    const pbxConfig = fromKludgyConfig(kludgyConfig);
    pbxConfig.subscriptionDisplayId = subscriptionDisplayId;

    dispatch(changeSubscriptionPbxConfiguration(subscriptionId, pbxConfig, getErrorsFromPbxNumberSettings));
  };

  if (editing) {
    return (
      <PbxNumberSettingsEditor
        subscriptionPbxConfiguration={subscriptionPbxConfiguration}
        numberRange={numberRange}
        extensionNumberRange={extensionNumberRange}
        onConfirm={onConfirmEdit}
        onCancel={onCancel}
        saving={saving}
      />
    );
  }

  return (
    <PbxNumberSettingsView subscriptionPbxConfiguration={subscriptionPbxConfiguration} onStartEdit={onStartEdit} />
  );
};
